import { adaptiveLevelsApi } from "@api/adaptiveLevels"
import { homeKeys } from "@features/home"
import { analytics } from "@lib/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { QueryClient, QueryKey } from "@tanstack/react-query"

import { adaptiveLevelKeys } from "./queryKeys"

import { DEFAULT_ADAPTIVE_PAGE_SIZE } from "@/constants/adaptiveLevels"
import type { AdaptiveLevel, AdaptiveMode } from "@/types"

export const useClassroomAdaptiveLevels = (
  classroomId: string | undefined,
  page = 1,
  pageSize = DEFAULT_ADAPTIVE_PAGE_SIZE,
) =>
  useQuery({
    queryKey: adaptiveLevelKeys.classroom(classroomId ?? "", page, pageSize),
    queryFn: () =>
      adaptiveLevelsApi.listClassroom(classroomId as string, {
        page,
        page_size: pageSize,
      }),
    enabled: !!classroomId,
    // Paging inside the dialog keeps the previous page on screen instead of
    // blanking the list — but never another classroom's rows or count.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === classroomId ? previous : undefined,
  })

export const useStudentAdaptiveLevel = (
  studentId: string | undefined,
  historyPage = 1,
  historyPageSize = 50,
) =>
  useQuery({
    queryKey: adaptiveLevelKeys.student(studentId ?? "", historyPage, historyPageSize),
    queryFn: () =>
      adaptiveLevelsApi.getStudent(studentId as string, {
        page: historyPage,
        page_size: historyPageSize,
      }),
    enabled: !!studentId,
  })

const invalidateKeys = (queryClient: QueryClient, keys: readonly QueryKey[]) =>
  Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))).then(
    () => undefined,
  )

// Inline literals are the students / classrooms / student-profile factories'
// prefixes: those features are not importable from here without a cycle
// (students imports this feature). `homeKeys` is: Home's "niveles por
// revisar" row counts the same recommendations.
const invalidateClassroomAdaptiveProjections = (queryClient: QueryClient, classroomId: string) =>
  invalidateKeys(queryClient, [
    adaptiveLevelKeys.classroomPrefix(classroomId),
    adaptiveLevelKeys.students(),
    ["students", "list"],
    ["classrooms"],
    ["student-profile"],
    homeKeys.all,
  ])

const invalidateStudentAdaptiveProjections = (
  queryClient: QueryClient,
  classroomId: string,
  studentId: string,
) =>
  invalidateKeys(queryClient, [
    adaptiveLevelKeys.classroomPrefix(classroomId),
    adaptiveLevelKeys.studentPrefix(studentId),
    ["students", "list"],
    ["students", "progress", studentId],
    ["student-profile"],
    homeKeys.all,
  ])

export const useSetAdaptiveMode = (classroomId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mode: AdaptiveMode) => adaptiveLevelsApi.setMode(classroomId, mode),
    onSuccess: (_response, mode) => {
      analytics.track("adaptive_level_changed", {
        action: "mode_set",
        classroom_id: classroomId,
        mode,
      })
      return invalidateClassroomAdaptiveProjections(queryClient, classroomId)
    },
  })
}

export const useAcceptAdaptiveRecommendation = (classroomId: string, studentId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (evaluationId: string) => adaptiveLevelsApi.accept(studentId, evaluationId),
    // One event for all five adaptive-level actions, told apart by `action`;
    // `studentId` and the free-text `reason` are never sent.
    onSuccess: () => {
      analytics.track("adaptive_level_changed", { action: "accepted", classroom_id: classroomId })
      return invalidateStudentAdaptiveProjections(queryClient, classroomId, studentId)
    },
  })
}

export const useRejectAdaptiveRecommendation = (classroomId: string, studentId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ evaluationId, reason }: { evaluationId: string; reason: string }) =>
      adaptiveLevelsApi.reject(studentId, evaluationId, reason),
    onSuccess: () => {
      analytics.track("adaptive_level_changed", { action: "rejected", classroom_id: classroomId })
      return invalidateStudentAdaptiveProjections(queryClient, classroomId, studentId)
    },
  })
}

export const useOverrideAdaptiveLevel = (classroomId: string, studentId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      idempotency_key: string
      target_level: AdaptiveLevel
      reason: string
      pin: boolean
    }) => adaptiveLevelsApi.override(studentId, payload),
    onSuccess: () => {
      analytics.track("adaptive_level_changed", { action: "overridden", classroom_id: classroomId })
      return invalidateStudentAdaptiveProjections(queryClient, classroomId, studentId)
    },
  })
}

export const useSetAdaptivePin = (classroomId: string, studentId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { pinned: boolean; reason: string }) =>
      payload.pinned
        ? adaptiveLevelsApi.pin(studentId, payload.reason)
        : adaptiveLevelsApi.unpin(studentId, payload.reason),
    onSuccess: () => {
      analytics.track("adaptive_level_changed", {
        action: "pin_changed",
        classroom_id: classroomId,
      })
      return invalidateStudentAdaptiveProjections(queryClient, classroomId, studentId)
    },
  })
}

/** Undo the professor's latest accept / override (the status line's
 *  "Deshacer"). The server refuses with 409 `adaptive_undo_unavailable` once
 *  anything newer happened; the caller hides the control then. */
export const useUndoAdaptiveDecision = (classroomId: string, studentId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => adaptiveLevelsApi.undo(studentId),
    onSuccess: () => {
      analytics.track("adaptive_level_changed", { action: "undone", classroom_id: classroomId })
      return invalidateStudentAdaptiveProjections(queryClient, classroomId, studentId)
    },
  })
}
